import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import MarketingLayout from "../marketing/components/MarketingLayout";
import { PRICING } from "../marketing/data/pricing";

const MODULE_OPTIONS = [
  { key: "profile", label: "ASAP PROFIL" },
  { key: "partners", label: "ASAP PARTNERS" },
  { key: "skills", label: "ASAP SKILLS" },
  { key: "pilot", label: "ASAP PILOT" },
  { key: "cv", label: "ASAP CV" },
];

const labelForKey = (key) => MODULE_OPTIONS.find((m) => m.key === key)?.label;

const buildInitialForm = (searchParams) => {
  const moduleKey = searchParams.get("module");
  const tierKey = searchParams.get("tier");
  const preLabel = labelForKey(moduleKey);
  const tier = moduleKey && tierKey ? PRICING[moduleKey]?.tiers.find((t) => t.key === tierKey) : null;
  return {
    firstName: "",
    name: "",
    email: "",
    business: "",
    phone: "",
    modules: preLabel ? [preLabel] : [],
    modulesOther: false,
    modulesOtherText: "",
    comment: tier ? `Bonjour, je suis intéressé(e) par l'offre ${PRICING[moduleKey].label} - ${tier.name} (${tier.price}${tier.suffix || ""}).` : "",
  };
};

const Contact = () => {
  const [searchParams] = useSearchParams();
  const initial = useMemo(() => buildInitialForm(searchParams), [searchParams]);
  const [form, setForm] = useState(initial);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError("");
  };

  const toggleModule = (label) => {
    setForm((prev) => {
      const has = prev.modules.includes(label);
      return { ...prev, modules: has ? prev.modules.filter((m) => m !== label) : [...prev.modules, label] };
    });
    if (submitError) setSubmitError("");
  };

  const toggleOther = (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({ ...prev, modulesOther: checked, modulesOtherText: checked ? prev.modulesOtherText : "" }));
    if (submitError) setSubmitError("");
  };

  const validate = () => {
    const { modules, modulesOther, modulesOtherText, firstName, name, email, business, phone } = form;
    if (!modules.length && !modulesOther) return "Veuillez sélectionner au moins un module";
    if (modulesOther && !modulesOtherText.trim()) return "Merci de préciser le module \"Autre\"";
    if (!firstName.trim()) return "Le prénom est requis";
    if (!name.trim()) return "Le nom est requis";
    if (!email.trim()) return "L'email est requis";
    if (!business.trim()) return "L'entreprise est requise";
    if (!phone.trim()) return "Le numéro de téléphone est requis";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "L'adresse email n'est pas valide";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/api/contact`,
        form,
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 200 || res.status === 201) {
        setSubmitSuccess(true);
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      const msg = error.response?.data?.message || "Une erreur est survenue lors de l'envoi. Veuillez réessayer.";
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MarketingLayout showFinalCta={false} variant="contact">
      <Helmet>
        <title>Contactez-nous - ASAP Hub</title>
        <meta name="description" content="Demandez une démo personnalisée d'ASAP Hub : Profile, Partners, Skills, Pilot, CV. Réponse sous 48h." />
      </Helmet>

      <section className="mk-contact-section" data-testid="contact-page">
        <div className="mk-wrap mk-contact-grid">
          <aside className="mk-reveal">
            <h1>Parlons de votre projet</h1>
            <p className="mk-lead">Décrivez vos besoins, choisissez les modules qui vous intéressent et l'équipe ASAP Hub vous recontacte sous 48 heures.</p>
            <div className="mk-info-list">
              <div className="mk-info-item">
                <i aria-hidden="true">@</i>
                <div><b>Email</b><a href="mailto:hello@asap-cv.fr">hello@asap-cv.fr</a></div>
              </div>
              <div className="mk-info-item">
                <i aria-hidden="true">⏱</i>
                <div><b>Délai de réponse</b>Sous 48 heures ouvrées</div>
              </div>
              <div className="mk-info-item">
                <i aria-hidden="true">✓</i>
                <div><b>Période d'essai</b>Démo gratuite et personnalisée</div>
              </div>
            </div>
          </aside>

          <div className="mk-contact-card mk-reveal">
            {submitSuccess ? (
              <div className="mk-form-success-panel" data-testid="contact-success">
                <div className="mk-check-circle" aria-hidden="true">✓</div>
                <h3>Merci pour votre demande !</h3>
                <p style={{ color: "#b6c5d9", margin: "10px 0 24px" }}>
                  Votre message a bien été envoyé. Notre équipe vous recontactera rapidement.
                </p>
                <Link to="/" className="mk-btn mk-btn-primary">Retour à l'accueil</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <h3>Demander une démo</h3>
                {submitError && (
                  <div className="mk-form-error" role="alert" data-testid="contact-error">{submitError}</div>
                )}

                <div className="mk-form-row">
                  <div className="mk-field">
                    <label htmlFor="firstName">Prénom <span className="mk-required">*</span></label>
                    <input id="firstName" name="firstName" value={form.firstName} onChange={handleInput} disabled={isSubmitting} autoComplete="given-name" />
                  </div>
                  <div className="mk-field">
                    <label htmlFor="name">Nom <span className="mk-required">*</span></label>
                    <input id="name" name="name" value={form.name} onChange={handleInput} disabled={isSubmitting} autoComplete="family-name" />
                  </div>
                </div>

                <div className="mk-field">
                  <label htmlFor="email">Email professionnel <span className="mk-required">*</span></label>
                  <input id="email" name="email" type="email" value={form.email} onChange={handleInput} disabled={isSubmitting} autoComplete="email" />
                </div>

                <div className="mk-form-row">
                  <div className="mk-field">
                    <label htmlFor="business">Entreprise <span className="mk-required">*</span></label>
                    <input id="business" name="business" value={form.business} onChange={handleInput} disabled={isSubmitting} autoComplete="organization" />
                  </div>
                  <div className="mk-field">
                    <label htmlFor="phone">Téléphone <span className="mk-required">*</span></label>
                    <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleInput} disabled={isSubmitting} autoComplete="tel" />
                  </div>
                </div>

                <div className="mk-field">
                  <label>Modules souhaités <span className="mk-required">*</span></label>
                  <div className="mk-modules-grid">
                    {MODULE_OPTIONS.map((opt) => (
                      <label key={opt.key} className="mk-module-check" data-testid={`module-checkbox-${opt.key}`}>
                        <input
                          type="checkbox"
                          checked={form.modules.includes(opt.label)}
                          onChange={() => toggleModule(opt.label)}
                          disabled={isSubmitting}
                        />
                        {opt.label}
                      </label>
                    ))}
                    <label className="mk-module-check">
                      <input
                        type="checkbox"
                        checked={form.modulesOther}
                        onChange={toggleOther}
                        disabled={isSubmitting}
                      />
                      Autre
                    </label>
                  </div>
                  {form.modulesOther && (
                    <input
                      style={{ marginTop: "10px" }}
                      name="modulesOtherText"
                      value={form.modulesOtherText}
                      onChange={handleInput}
                      disabled={isSubmitting}
                      placeholder="Précisez le module souhaité"
                    />
                  )}
                </div>

                <div className="mk-field">
                  <label htmlFor="comment">Commentaires</label>
                  <textarea id="comment" name="comment" value={form.comment} onChange={handleInput} disabled={isSubmitting} placeholder="Décrivez brièvement vos besoins" />
                </div>

                <button
                  type="submit"
                  className="mk-btn mk-btn-primary"
                  disabled={isSubmitting}
                  style={{ width: "100%", padding: "14px 18px" }}
                  data-testid="contact-submit"
                >
                  {isSubmitting ? "Envoi en cours…" : "Je souhaite être recontacté"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default Contact;
